import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { reloadWorkspaces, useWorkspace } from 'hooks/useWorkspace';

import authLighthouse from 'assets/AuthLighthouse.png';
import beaconLogoFull from 'assets/BeaconLogoFull.svg';

import { ROUTES } from 'routes/route.constants';

import FilesService from 'services/files';
import { inviteMembers } from 'services/membersService';
import { createWorkspace } from 'services/workspaceService';

import { getInitials, isValidEmail } from 'utlis/globalFunctions';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';

import styles from './styles.module.scss';

const CreateWorkspace = () => {
  const navigate = useNavigate();
  const authUser = useSelector(state => state.userStore);
  const { selectWorkspace } = useWorkspace();

  const [workspaceName, setWorkspaceName] = useState('');
  const [emails, setEmails] = useState([]);
  const [inviteInputValue, setInviteInputValue] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef(null);
  const inviteInputRef = useRef(null);

  useEffect(() => {
    if (!authUser?.is_internal_user) {
      navigate(ROUTES.TEAM_HOME, { replace: true });
    }
  }, [authUser?.is_internal_user, navigate]);

  if (!authUser?.is_internal_user) {
    return null;
  }

  const handleLogoClick = () => fileInputRef.current?.click();

  const handleLogoChange = e => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setLogoFile(file);

    const reader = new FileReader();

    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const addEmail = value => {
    const trimmed = value
      .trim()
      .replace(/,$/, '')
      .trim();

    if (!trimmed || emails.includes(trimmed)) {
      setInviteInputValue('');

      return;
    }

    setEmails(prev => [...prev, trimmed]);
    setInviteInputValue('');
  };

  const removeEmail = email => setEmails(prev => prev.filter(e => e !== email));

  const handleInviteKeyDown = e => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail(inviteInputValue);
    }
    else if (e.key === 'Backspace' && !inviteInputValue && emails.length > 0) {
      setEmails(prev => prev.slice(0, -1));
    }
  };

  const handleInviteBlur = () => {
    if (inviteInputValue.trim()) {
      addEmail(inviteInputValue);
    }
  };

  const handleInvitePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const parts = pasted.split(/[\s,;]+/).filter(Boolean);

    if (parts.length > 1) {
      const unique = parts.filter(p => !emails.includes(p));

      setEmails(prev => [...prev, ...unique]);
    }
    else {
      setInviteInputValue(prev => prev + pasted);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!workspaceName.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      let avatarUrl = null;

      if (logoFile) {
        avatarUrl = await FilesService.uploadFileFromLocal(logoFile);
      }

      const newWorkspace = await createWorkspace(workspaceName.trim(), avatarUrl);

      if (emails.length > 0) {
        await inviteMembers({ workspaceUuid: newWorkspace.uuid, emails, role: 'viewer' });
      }

      selectWorkspace(newWorkspace.uuid);
      reloadWorkspaces();
      successReactToastify('New workspace successfully created');
      navigate(ROUTES.TEAM_HOME);
      // No setIsSubmitting(false) on success — component unmounts on navigate.
      // Resetting here would cause a brief re-render with isSubmitting=false
      // before unmount, which can allow a second submission to slip through.
    }
    catch {
      errorReactToastify('Failed to create workspace. Please try again.');
      setIsSubmitting(false);
    }
  };

  const nameInitials = getInitials(workspaceName) === '?' ? '' : getInitials(workspaceName);

  return (
    <div className={styles.page}>
      {/* ── Left panel ── */}
      <div className={styles['left-panel']}>
        <header className={styles['logo-header']}>
          <img
            src={beaconLogoFull}
            alt="onBeacon"
          />
        </header>

        <main className={styles['form-content']}>
          <button
            type="button"
            className={styles['back-btn']}
            onClick={() => navigate(-1)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#606776"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>

          <div className={styles.heading}>
            <h1>
              Create a new workspace
            </h1>
            <p>
              A shared space for your team
            </p>
          </div>

          <form
            className={styles.form}
            onSubmit={handleSubmit}
          >
            {/* Logo */}
            <div className={styles['logo-section']}>
              <div className={styles['logo-label-row']}>
                <span className={styles['label-main']}>
                  Logo
                </span>
                <span className={styles['label-optional']}>
                  (Optional)
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoChange}
                onClick={e => {
                  e.target.value = null;
                }}
              />

              <button
                type="button"
                className={`${styles['logo-avatar']} cursor-pointer`}
                onClick={handleLogoClick}
                aria-label="Upload workspace logo"
              >
                {logoPreview
                  ? <img src={logoPreview} alt="workspace logo" />
                  : (
                    <span className={styles['avatar-initials']}>
                      {nameInitials || 'W'}
                    </span>
                  )}
              </button>
            </div>

            {/* Workspace name */}
            <div className={styles.field}>
              <label className={styles['field-label']}>
                Workspace name
              </label>
              <input
                className={styles['field-input']}
                type="text"
                placeholder="Rick's team"
                value={workspaceName}
                onChange={e => setWorkspaceName(e.target.value)}
              />
            </div>

            {/* Invite members */}
            <div className={styles.field}>
              <label className={styles['field-label']}>
                Invite members
              </label>
              <div className={styles['invite-row']}>
                <div
                  className={styles['invite-chip-container']}
                  onClick={() => inviteInputRef.current?.focus()}
                >
                  {emails.map(email => (
                    <span
                      key={email}
                      className={`${styles['invite-chip']} ${!isValidEmail(email) ? styles['invite-chip-error'] : ''}`}
                    >
                      {email}
                      <button
                        type="button"
                        className={styles['invite-chip-remove']}
                        onClick={e => {
                          e.stopPropagation();
                          removeEmail(email);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    ref={inviteInputRef}
                    type="email"
                    className={styles['invite-chip-text-input']}
                    placeholder={emails.length === 0 ? 'Emails, separated by commas' : ''}
                    value={inviteInputValue}
                    onChange={e => setInviteInputValue(e.target.value)}
                    onKeyDown={handleInviteKeyDown}
                    onBlur={handleInviteBlur}
                    onPaste={handleInvitePaste}
                  />
                </div>
                <div className={styles['invite-role-label']}>
                  Can view
                </div>
              </div>
              <span className={styles['field-help']}>
                Can access the audits, but can&apos;t create a new one
              </span>
            </div>

            <button
              type="submit"
              className={styles['submit-btn']}
              disabled={!workspaceName.trim() || isSubmitting || emails.some(e => !isValidEmail(e))}
            >
              {isSubmitting ? 'Creating...' : 'Create workspace'}
            </button>
          </form>
        </main>

        <footer className={styles.footer}>
          Read our
          {' '}
          <a href={ROUTES.TERMS_AND_CONDITIONS}>
            Terms &amp; Conditions
          </a>
          {' '}
          and
          {' '}
          <a href={ROUTES.PRIVACY_POLICY}>
            Privacy Policy
          </a>
        </footer>
      </div>

      {/* ── Right panel ── */}
      <div className={styles['right-panel']} style={{ backgroundImage: `url(${authLighthouse})` }}>
        <div className={styles['preview-card']}>
          {/* Sidebar */}
          <div className={styles['mock-sidebar']}>
            <div className={styles['mock-sidebar-logo']}>
              <div className={styles['mock-logo-label']} />
              <div className={styles['mock-logo-icon']} />
            </div>
            <div className={styles['mock-sidebar-divider']} />
            <div className={styles['mock-sidebar-nav']}>
              <div className={styles['mock-workspace-row']}>
                <div className={styles['mock-ws-avatar']}>
                  {logoPreview
                    ? <img src={logoPreview} alt="" />
                    : (nameInitials || 'W')}
                </div>
                <span className={styles['mock-ws-name']}>
                  {workspaceName || 'Workspace name'}
                </span>
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  className={styles['mock-ws-chevron']}
                >
                  <path
                    d="M9 12L15 18L21 12"
                    stroke="#8F97A5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={`${styles['mock-nav-item']} ${styles['mock-nav-active']}`}>
                <div className={styles['mock-nav-icon-active']} />
                <div className={styles['mock-nav-label']} />
              </div>
              <div className={styles['mock-nav-item']}>
                <div className={styles['mock-nav-icon']} />
                <div className={styles['mock-nav-label-short']} />
              </div>
            </div>
          </div>

          {/* Main content panel */}
          <div className={styles['mock-main']}>
            <div className={styles['mock-header']}>
              <div className={styles['mock-header-label']} />
            </div>
          </div>
        </div>

        <div className={styles['right-panel-gradient']} />
      </div>
    </div>
  );
};

export default CreateWorkspace;
